// src/pages/Community.tsx
import React, { useState } from "react";
import {
  Bell,
  MessageSquare,
  Users,
  MapPin,
  ChevronRight,
  ThumbsUp,
  Clock,
  Pin,
  Search,
  Megaphone,
  Globe,
  BookOpen,
  Star,
  Lock,
  Send,
  ChevronDown,
  ChevronUp,
  Calendar,
  Flame,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useAppSelector } from "../../app/hooks";
import {
  useCreatePostMutation,
  useCreateReplyMutation,
  useGetAnnouncementsQuery,
  useGetDirectoryQuery,
  useGetDiscussionPostsQuery,
  useGetRegionalUpdatesQuery,
  useGetRepliesQuery,
  useToggleLikeMutation,
  type Announcement,
  type AnnouncementCategory,
  type DiscussionPost,
  type DiscussionReply,
  type DirectoryMember,
  type RegionalUpdate,
} from "../../app/api/communityApi";

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */

type CommunityTab = "announcements" | "discussion" | "directory" | "regional";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */

const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">
    {children}
  </h2>
);

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const fmtRelative = (d?: string | null) => {
  if (!d) return "—";
  const now = new Date();
  const date = new Date(d);
  const diff = now.getTime() - date.getTime();
  const mins = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 8) return `${days}d ago`;
  return fmtDate(d);
};

const getInitials = (
  firstName?: string | null,
  lastName?: string | null,
  fallback = "U",
) => {
  const first = firstName?.[0] ?? "";
  const last = lastName?.[0] ?? "";
  return `${first}${last}`.toUpperCase() || fallback;
};

const avatarColourClasses = [
  "bg-blue-500",
  "bg-rose-500",
  "bg-green-600",
  "bg-purple-500",
  "bg-amber-500",
  "bg-cyan-500",
  "bg-indigo-500",
  "bg-pink-500",
  "bg-emerald-600",
];

const colourFromString = (value: string) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return avatarColourClasses[Math.abs(hash) % avatarColourClasses.length];
};

const prettyTier = (tier?: string | null) => {
  const map: Record<string, string> = {
    wider_audience: "Wider Audience",
    inner_circle: "Inner Circle",
    legacy_fellows: "Legacy Fellows",
  };
  return tier ? (map[tier] ?? tier) : "Member";
};

const TIER_META: Record<string, { colour: string; bg: string }> = {
  wider_audience: { colour: "text-green-700", bg: "bg-green-50" },
  inner_circle: { colour: "text-blue-700", bg: "bg-blue-50" },
  legacy_fellows: { colour: "text-amber-700", bg: "bg-amber-50" },
  "Wider Audience": { colour: "text-green-700", bg: "bg-green-50" },
  "Inner Circle": { colour: "text-blue-700", bg: "bg-blue-50" },
  "Legacy Fellows": { colour: "text-amber-700", bg: "bg-amber-50" },
};

const ANNOUNCEMENT_META: Record<
  AnnouncementCategory,
  { label: string; colour: string; bg: string; icon: React.ReactNode }
> = {
  event: {
    label: "Event",
    colour: "text-purple-700",
    bg: "bg-purple-100",
    icon: <Calendar size={12} />,
  },
  deadline: {
    label: "Deadline",
    colour: "text-red-700",
    bg: "bg-red-100",
    icon: <Clock size={12} />,
  },
  upload: {
    label: "Upload",
    colour: "text-blue-700",
    bg: "bg-blue-100",
    icon: <BookOpen size={12} />,
  },
  fellows: {
    label: "Fellows",
    colour: "text-amber-700",
    bg: "bg-amber-100",
    icon: <Star size={12} />,
  },
  general: {
    label: "General",
    colour: "text-gray-600",
    bg: "bg-gray-100",
    icon: <Megaphone size={12} />,
  },
};

const TOPIC_META: Record<
  string,
  { label: string; colour: string; bg: string }
> = {
  governance: {
    label: "Governance",
    colour: "text-green-700",
    bg: "bg-green-100",
  },
  ethics: { label: "Ethics", colour: "text-blue-700", bg: "bg-blue-100" },
  roots: { label: "Roots", colour: "text-amber-700", bg: "bg-amber-100" },
  marketplace: {
    label: "Marketplace",
    colour: "text-purple-700",
    bg: "bg-purple-100",
  },
  general: { label: "General", colour: "text-gray-600", bg: "bg-gray-100" },
};

/* ─────────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────────── */

const AnnouncementCard: React.FC<{ item: Announcement }> = ({ item }) => {
  const [expanded, setExpanded] = useState(item.isPinned);
  const meta = ANNOUNCEMENT_META[item.category];

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all
      ${item.isPinned ? "border-green-200" : "border-gray-100 hover:border-gray-200"}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-5 py-4 flex items-start gap-3 group"
      >
        <span
          className={`shrink-0 mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.bg} ${meta.colour}`}
        >
          {meta.icon}
          {meta.label}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              {item.isPinned && (
                <Pin size={11} className="text-green-500 shrink-0" />
              )}
              <h3 className="text-sm font-semibold text-gray-900 leading-snug group-hover:text-green-700 transition-colors">
                {item.title}
              </h3>
              {item.isNew && (
                <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-500 text-white uppercase">
                  New
                </span>
              )}
            </div>
            {expanded ? (
              <ChevronUp size={14} className="shrink-0 text-gray-400 mt-0.5" />
            ) : (
              <ChevronDown
                size={14}
                className="shrink-0 text-gray-400 mt-0.5"
              />
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-400 flex-wrap">
            <span>{item.authorDisplayName}</span>
            <span>·</span>
            <span>{fmtDate(item.publishedAt || item.createdAt)}</span>
            {item.quarter && (
              <>
                <span>·</span>
                <span>{item.quarter}</span>
              </>
            )}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5">
          <div
            className={`rounded-xl p-4 text-sm text-gray-700 leading-relaxed
            ${item.isPinned ? "bg-green-50 border border-green-100" : "bg-gray-50"}`}
          >
            {item.body}
          </div>
        </div>
      )}
    </div>
  );
};

const DiscussionReplies: React.FC<{ postId: string }> = ({ postId }) => {
  const { data, isLoading, isFetching } = useGetRepliesQuery(postId);
  const [createReply, { isLoading: isCreatingReply }] =
    useCreateReplyMutation();
  const [replyBody, setReplyBody] = useState("");

  const replies = data?.data ?? [];

  const handleCreateReply = async () => {
    const body = replyBody.trim();
    if (!body) return;

    try {
      await createReply({ postId, body }).unwrap();
      setReplyBody("");
    } catch {
      // ignore UI error here
    }
  };

  return (
    <div className="border-t border-gray-50 px-5 py-4 space-y-4 bg-gray-50/50">
      <div className="space-y-3">
        {isLoading ? (
          <>
            <Bone className="h-16" />
            <Bone className="h-16" />
          </>
        ) : replies.length === 0 ? (
          <p className="text-xs text-gray-400">No replies yet.</p>
        ) : (
          replies.map((reply: DiscussionReply) => {
            const fullName = `${reply.author.firstName} ${reply.author.lastName}`;
            const initials = getInitials(
              reply.author.firstName,
              reply.author.lastName,
              "R",
            );
            return (
              <div
                key={reply.id}
                className="bg-white rounded-xl border border-gray-100 px-4 py-3"
              >
                <div className="flex items-start gap-3">
                  {reply.author.avatar ? (
                    <img
                      src={reply.author.avatar}
                      alt={fullName}
                      className="w-8 h-8 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div
                      className={`w-8 h-8 rounded-full ${colourFromString(
                        fullName,
                      )} flex items-center justify-center text-white text-[11px] font-bold shrink-0`}
                    >
                      {initials}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-xs font-semibold text-gray-900">
                          {fullName}
                        </p>
                        <p className="text-[11px] text-gray-400">
                          {reply.author.ministryRole || "Member"} ·{" "}
                          {reply.author.region || "Region not set"}
                        </p>
                      </div>
                      <span className="text-[11px] text-gray-400 shrink-0">
                        {fmtRelative(reply.createdAt)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-700 leading-relaxed mt-2">
                      {reply.body}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="space-y-2">
        <textarea
          placeholder="Write a reply…"
          rows={3}
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          className="w-full text-sm text-gray-700 bg-white rounded-xl px-4 py-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-300 placeholder-gray-300 resize-none leading-relaxed transition"
        />
        <div className="flex justify-between items-center gap-3">
          <span className="text-[11px] text-gray-400">
            {isFetching
              ? "Refreshing replies…"
              : `${replies.length} repl${replies.length === 1 ? "y" : "ies"}`}
          </span>
          <button
            onClick={handleCreateReply}
            disabled={!replyBody.trim() || isCreatingReply}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isCreatingReply ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Posting…
              </>
            ) : (
              <>
                <Send size={12} />
                Reply
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const DiscussionCard: React.FC<{
  post: DiscussionPost;
  likedByMe: boolean;
  likesCount: number;
  onLike: (id: string, currentlyLiked: boolean, currentCount: number) => void;
}> = ({ post, likedByMe, likesCount, onLike }) => {
  const [expanded, setExpanded] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const topic = TOPIC_META[post.topic] ?? TOPIC_META.general;
  const authorName = `${post.author.firstName} ${post.author.lastName}`;
  const initials = getInitials(post.author.firstName, post.author.lastName);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:border-gray-200 hover:shadow-md transition-all duration-200">
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          {post.author.avatar ? (
            <img
              src={post.author.avatar}
              alt={authorName}
              className="shrink-0 w-9 h-9 rounded-full object-cover"
            />
          ) : (
            <div
              className={`shrink-0 w-9 h-9 rounded-full ${colourFromString(
                authorName,
              )} flex items-center justify-center text-white text-xs font-bold`}
            >
              {initials}
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-900">{authorName}</p>
            <p className="text-[11px] text-gray-400 truncate">
              {post.author.ministryRole || "Member"} ·{" "}
              {post.author.region || "Region not set"}
            </p>
          </div>

          <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${topic.bg} ${topic.colour}`}
            >
              {topic.label}
            </span>
            {post.quarter && (
              <span className="text-[10px] text-gray-400">{post.quarter}</span>
            )}
            {post.isPinned && <Pin size={11} className="text-green-500" />}
          </div>
        </div>

        <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-2">
          {post.title}
        </h3>
        <p
          className={`text-xs text-gray-600 leading-relaxed ${
            !expanded ? "line-clamp-3" : ""
          }`}
        >
          {post.body}
        </p>
        {post.body.length > 180 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] font-semibold text-green-600 hover:text-green-700 mt-1"
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
      </div>

      <div className="px-5 py-3 border-t border-gray-50 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onLike(post.id, likedByMe, likesCount)}
            className={`flex items-center gap-1.5 text-xs font-medium transition-colors
              ${likedByMe ? "text-green-600" : "text-gray-400 hover:text-green-600"}`}
          >
            <ThumbsUp size={13} className={likedByMe ? "fill-current" : ""} />
            {likesCount}
          </button>

          <button
            onClick={() => setShowReplies((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-blue-600 transition-colors"
          >
            <MessageSquare size={13} />
            {post.replyCount} {post.replyCount === 1 ? "reply" : "replies"}
            {showReplies ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>

        <span className="text-[11px] text-gray-400">
          {fmtRelative(post.createdAt)}
        </span>
      </div>

      {showReplies && <DiscussionReplies postId={post.id} />}
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */

const Community: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user);
  const [activeTab, setActiveTab] = useState<CommunityTab>("announcements");

  /* Announcements */
  const {
    data: announcementsRes,
    isLoading: announcementsLoading,
    isError: announcementsError,
  } = useGetAnnouncementsQuery({});

  const announcements = announcementsRes?.data ?? [];
  const pinnedAnnouncements = announcements.filter((a) => a.isPinned);
  const recentAnnouncements = announcements.filter((a) => !a.isPinned);
  const newCount = announcements.filter((a) => a.isNew).length;

  /* Discussion */
  const [discussionPage, setDiscussionPage] = useState(1);
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState("");
  const [newPostBody, setNewPostBody] = useState("");
  const [newPostTopic, setNewPostTopic] = useState("general");
  const [likeOverrides, setLikeOverrides] = useState<
    Record<string, { likedByMe: boolean; likesCount: number }>
  >({});

  const {
    data: postsRes,
    isLoading: postsLoading,
    isFetching: postsFetching,
    isError: postsError,
  } = useGetDiscussionPostsQuery({
    limit: 20,
    page: discussionPage,
  });

  const [toggleLike] = useToggleLikeMutation();
  const [createPost, { isLoading: creatingPost }] = useCreatePostMutation();

  const discussionPosts = postsRes?.data ?? [];

  const handleLike = async (
    id: string,
    currentlyLiked: boolean,
    currentCount: number,
  ) => {
    const optimistic = {
      likedByMe: !currentlyLiked,
      likesCount: currentlyLiked ? currentCount - 1 : currentCount + 1,
    };

    setLikeOverrides((prev) => ({ ...prev, [id]: optimistic }));

    try {
      const res = await toggleLike(id).unwrap();
      setLikeOverrides((prev) => ({
        ...prev,
        [id]: { likedByMe: res.liked, likesCount: res.likesCount },
      }));
    } catch {
      setLikeOverrides((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }
  };

  const handleCreatePost = async () => {
    const title = newPostTitle.trim();
    const body = newPostBody.trim();
    if (!title || !body) return;

    try {
      await createPost({
        title,
        body,
        topic: newPostTopic as any,
      }).unwrap();

      setNewPostTitle("");
      setNewPostBody("");
      setNewPostTopic("general");
      setNewPostOpen(false);
      setDiscussionPage(1);
    } catch {
      // ignore here
    }
  };

  /* Regional */
  const {
    data: regionalRes,
    isLoading: regionalLoading,
    isError: regionalError,
  } = useGetRegionalUpdatesQuery({});

  const regionalUpdates = regionalRes?.data ?? [];
  const myRegionalUpdates = regionalUpdates.filter((r) => r.isMyRegion);
  const otherRegionalUpdates = regionalUpdates.filter((r) => !r.isMyRegion);

  /* Directory */
  const [searchDir, setSearchDir] = useState("");
  const [directoryPage, setDirectoryPage] = useState(1);

  const {
    data: directoryRes,
    isLoading: directoryLoading,
    isFetching: directoryFetching,
    isError: directoryError,
  } = useGetDirectoryQuery({
    search: searchDir.trim() || undefined,
    limit: 12,
    page: directoryPage,
  });

  const directoryMembers = directoryRes?.data ?? [];
  const directoryIsFullAccess = directoryRes?.isFullAccess ?? false;

  const TABS: {
    key: CommunityTab;
    label: string;
    icon: React.ReactNode;
    badge?: number;
  }[] = [
    {
      key: "announcements",
      label: "Announcements",
      icon: <Bell size={14} />,
      badge: newCount,
    },
    {
      key: "discussion",
      label: "Discussion",
      icon: <MessageSquare size={14} />,
    },
    {
      key: "regional",
      label: "Regional",
      icon: <Globe size={14} />,
    },
    {
      key: "directory",
      label: "Directory",
      icon: <Users size={14} />,
    },
  ];

  return (
    <div className="space-y-6 pb-12">
      <div>
        <p className="text-xs font-medium uppercase tracking-widest text-blue-600 mb-1">
          YAYA Continent 2
        </p>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
          Community
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Stay connected with YAYA members across Continent 2 — announcements,
          discussions, regional updates, and member discovery.
        </p>
      </div>

      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 flex-wrap">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg transition-all duration-150 flex-1 justify-center min-w-fit
              ${
                activeTab === tab.key
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
          >
            <span
              className={
                activeTab === tab.key ? "text-blue-600" : "text-gray-400"
              }
            >
              {tab.icon}
            </span>
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full
                ${activeTab === tab.key ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"}`}
              >
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === "announcements" && (
        <div className="space-y-5">
          {announcementsLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Bone key={i} className="h-28" />
              ))}
            </div>
          ) : announcementsError ? (
            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              <AlertCircle size={14} />
              Could not load announcements.
            </div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              No announcements available right now.
            </div>
          ) : (
            <>
              {pinnedAnnouncements.length > 0 && (
                <div>
                  <SectionHeading>Pinned</SectionHeading>
                  <div className="space-y-3">
                    {pinnedAnnouncements.map((a) => (
                      <AnnouncementCard key={a.id} item={a} />
                    ))}
                  </div>
                </div>
              )}

              {recentAnnouncements.length > 0 && (
                <div>
                  <SectionHeading>Recent</SectionHeading>
                  <div className="space-y-3">
                    {recentAnnouncements.map((a) => (
                      <AnnouncementCard key={a.id} item={a} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === "discussion" && (
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <button
              onClick={() => setNewPostOpen(!newPostOpen)}
              className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
            >
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.firstName || "You"}
                  className="w-9 h-9 rounded-full object-cover shrink-0"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {getInitials(user?.firstName, user?.lastName, "U")}
                </div>
              )}
              <span className="flex-1 text-sm text-gray-400 italic">
                Share a reflection, question, or resource with the community…
              </span>
              <Send size={15} className="text-gray-300 shrink-0" />
            </button>

            {newPostOpen && (
              <div className="border-t border-gray-50 px-5 py-4 space-y-3">
                <input
                  type="text"
                  placeholder="Post title…"
                  value={newPostTitle}
                  onChange={(e) => setNewPostTitle(e.target.value)}
                  className="w-full text-sm font-semibold text-gray-800 bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-green-300 placeholder-gray-300"
                />
                <textarea
                  placeholder="Your reflection, question, or insight…"
                  rows={4}
                  value={newPostBody}
                  onChange={(e) => setNewPostBody(e.target.value)}
                  className="w-full text-sm text-gray-700 bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-green-300 placeholder-gray-300 resize-none leading-relaxed"
                />
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <select
                    value={newPostTopic}
                    onChange={(e) => setNewPostTopic(e.target.value)}
                    className="text-xs font-medium text-gray-600 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-green-300"
                  >
                    {Object.entries(TOPIC_META).map(([key, val]) => (
                      <option key={key} value={key}>
                        {val.label}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setNewPostOpen(false)}
                      className="px-4 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreatePost}
                      disabled={
                        !newPostTitle.trim() ||
                        !newPostBody.trim() ||
                        creatingPost
                      }
                      className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 active:scale-95 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creatingPost ? (
                        <>
                          <Loader2 size={12} className="animate-spin" />
                          Posting…
                        </>
                      ) : (
                        <>
                          <Send size={12} /> Post
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {postsLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Bone key={i} className="h-48" />
              ))}
            </div>
          ) : postsError ? (
            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              <AlertCircle size={14} />
              Could not load discussion posts.
            </div>
          ) : discussionPosts.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              No discussion posts yet.
            </div>
          ) : (
            <div className="space-y-3">
              {discussionPosts.map((post) => {
                const override = likeOverrides[post.id];
                return (
                  <DiscussionCard
                    key={post.id}
                    post={post}
                    likedByMe={override?.likedByMe ?? post.likedByMe}
                    likesCount={override?.likesCount ?? post.likesCount}
                    onLike={handleLike}
                  />
                );
              })}
            </div>
          )}

          {postsRes && postsRes.page < postsRes.pages && (
            <button
              onClick={() => setDiscussionPage((p) => p + 1)}
              disabled={postsFetching}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {postsFetching ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Loading…
                </>
              ) : (
                <>
                  Load more posts <ChevronRight size={14} />
                </>
              )}
            </button>
          )}
        </div>
      )}

      {activeTab === "regional" && (
        <div className="space-y-4">
          {regionalLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Bone key={i} className="h-36" />
              ))}
            </div>
          ) : regionalError ? (
            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              <AlertCircle size={14} />
              Could not load regional updates.
            </div>
          ) : regionalUpdates.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              No regional updates available right now.
            </div>
          ) : (
            <>
              {myRegionalUpdates.map((r: RegionalUpdate) => (
                <div
                  key={r.id}
                  className="relative overflow-hidden rounded-2xl border-2 border-green-300 bg-green-50 p-5 shadow-sm"
                >
                  <div className="absolute top-3 right-4">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500 text-white uppercase tracking-wide">
                      Your region
                    </span>
                  </div>
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className={`shrink-0 w-10 h-10 rounded-xl ${colourFromString(
                        `${r.author.firstName} ${r.author.lastName} ${r.region}`,
                      )} flex items-center justify-center text-white text-sm font-bold`}
                    >
                      {getInitials(r.author.firstName, r.author.lastName, "R")}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {r.region}
                      </p>
                      <p className="text-xs text-gray-500">
                        {r.author.firstName} {r.author.lastName} · Regional
                        Representative
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {fmtDate(r.createdAt)}
                        {r.quarter ? ` · ${r.quarter}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white/70 rounded-xl px-4 py-3">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {r.body}
                    </p>
                  </div>
                </div>
              ))}

              {otherRegionalUpdates.length > 0 && (
                <>
                  <SectionHeading>Other regions</SectionHeading>
                  <div className="space-y-3">
                    {otherRegionalUpdates.map((r: RegionalUpdate) => (
                      <div
                        key={r.id}
                        className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:border-gray-200 transition-colors"
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div
                            className={`shrink-0 w-9 h-9 rounded-xl ${colourFromString(
                              `${r.author.firstName} ${r.author.lastName} ${r.region}`,
                            )} flex items-center justify-center text-white text-xs font-bold`}
                          >
                            {getInitials(
                              r.author.firstName,
                              r.author.lastName,
                              "R",
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-semibold text-gray-900">
                                {r.region}
                              </p>
                              <span className="shrink-0 text-[11px] text-gray-400">
                                {fmtDate(r.createdAt)}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              {r.author.firstName} {r.author.lastName} ·
                              Regional Representative
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {r.body}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === "directory" && (
        <div className="space-y-5">
          {!directoryIsFullAccess && (
            <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
              <Lock size={14} className="text-blue-500 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-700 leading-relaxed">
                The full member directory is available to{" "}
                <strong>Inner Circle</strong> members and above. You are
                currently viewing a limited preview.
              </p>
            </div>
          )}

          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search by name, role, or region…"
              value={searchDir}
              onChange={(e) => {
                setSearchDir(e.target.value);
                setDirectoryPage(1);
              }}
              className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-transparent placeholder-gray-400 transition"
            />
          </div>

          {directoryLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((i) => (
                <Bone key={i} className="h-24" />
              ))}
            </div>
          ) : directoryError ? (
            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              <AlertCircle size={14} />
              Could not load the member directory.
            </div>
          ) : directoryMembers.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-400">
              No members match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {directoryMembers.map((m: DirectoryMember) => {
                const tierLabel = prettyTier(m.tier);
                const tierMeta = TIER_META[m.tier] ?? TIER_META[tierLabel];

                return (
                  <div
                    key={m._id}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 hover:border-gray-200 hover:shadow-md transition-all duration-150 group"
                  >
                    {m.avatar ? (
                      <img
                        src={m.avatar}
                        alt={`${m.firstName} ${m.lastName}`}
                        className="w-11 h-11 rounded-xl object-cover shrink-0"
                      />
                    ) : (
                      <div
                        className={`shrink-0 w-11 h-11 rounded-xl ${colourFromString(
                          `${m.firstName} ${m.lastName} ${m.region || ""}`,
                        )} flex items-center justify-center text-white text-sm font-bold`}
                      >
                        {getInitials(m.firstName, m.lastName, "M")}
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                        {m.firstName} {m.lastName}
                      </p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {m.ministryRole || "Member"}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tierMeta.bg} ${tierMeta.colour}`}
                        >
                          {tierLabel}
                        </span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                          <MapPin size={9} />
                          {m.region?.split("—")[1]?.trim() ??
                            m.region ??
                            "No region"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {directoryRes && directoryRes.page < directoryRes.pages && (
            <button
              onClick={() => setDirectoryPage((p) => p + 1)}
              disabled={directoryFetching}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {directoryFetching ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Loading…
                </>
              ) : (
                <>
                  Load more members <ChevronRight size={14} />
                </>
              )}
            </button>
          )}

          {!directoryIsFullAccess && (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm shrink-0">
                <Flame size={18} className="text-amber-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-700">
                  Unlock the full directory
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Progress to Inner Circle and above to access the complete
                  member network across Continent 2.
                </p>
              </div>
              <ChevronRight size={16} className="text-gray-300 shrink-0" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Community;
